// Domain constants + pure interpretation for Special Tests
// (TDT · SISI · ABLB). Shared by the live editor and the printed
// report so both surfaces stay in lock-step.

export const TDT_FREQS = [500, 1000, 2000, 4000];               // Hz
export const SISI_FREQS = [250, 500, 1000, 2000, 4000];         // Hz
export const ABLB_INTENSITIES = [20, 40, 60, 80];               // dB HL

export const ENV_OPTIONS = ['Sound Treated Booth', 'Free Field', 'Other'];
export const TRANSDUCER_OPTIONS = ['Insert Earphones', 'Supra-aural', 'Bone Conductor'];
export const EAR_OPTIONS = ['Right', 'Left', 'Bilateral'];

export const TDT_MAX_SECONDS = 60;
export const SISI_NORMAL_UPPER = 20;      // < 20 % → Normal
export const SISI_COCHLEAR_LOWER = 70;    // ≥ 70 % → Cochlear
export const ABLB_NO_RECRUIT_MAX = 5;     // interaural difference dB

export const DEFAULT_TDT_PROCEDURE =
  'Pure tone at 1,000 Hz presented at 5 dB SL for a maximum of 60 seconds. ' +
  'Patient indicates when the tone is no longer heard.';
export const DEFAULT_SISI_PROCEDURE =
  '1 dB increment tones presented at 20 dB SL. Patient indicates when a change in ' +
  'loudness is heard. 20 increments given at each test frequency.';
export const DEFAULT_ABLB_PROCEDURE =
  'Tone presented alternately to both ears in 5 dB steps. Patient adjusts intensity ' +
  'in the non-test ear to match loudness.';

// Immutable helpers -------------------------------------------------
const cloneWith = (obj, path, value) => {
  // path is [k1, k2, ...]. Sets value at deepest key, returns clone.
  const [head, ...rest] = path;
  const next = { ...(obj || {}) };
  if (rest.length === 0) {
    if (value === null || value === undefined || value === '') delete next[head];
    else next[head] = value;
  } else {
    next[head] = cloneWith(next[head] || {}, rest, value);
  }
  return next;
};

export const setTDT = (data, freq, seconds) =>
  ({ ...data, tdt: cloneWith(data.tdt || {}, ['values', String(freq)], seconds) });

export const setSISI = (data, ear, freq, pct) =>
  ({ ...data, sisi: cloneWith(data.sisi || {}, [ear, String(freq)], pct) });

export const setABLB = (data, ear, testInt, matchDb) =>
  ({ ...data, ablb: cloneWith(data.ablb || {}, [ear, String(testInt)], matchDb) });

// Normalise arbitrary saved data → strict shape (idempotent)
export function normaliseSpecial(data) {
  const d = data && typeof data === 'object' ? data : {};
  return {
    audiometer:  d.audiometer  || '',
    calibration: d.calibration || '',
    environment: ENV_OPTIONS.includes(d.environment) ? d.environment : 'Sound Treated Booth',
    transducer:  TRANSDUCER_OPTIONS.includes(d.transducer) ? d.transducer : 'Insert Earphones',
    test_ear:    EAR_OPTIONS.includes(d.test_ear) ? d.test_ear : 'Right',
    clinician:   d.clinician   || '',
    refer_by:    d.refer_by    || '',
    test_standards: d.test_standards || 'Routine Clinical Protocol',
    purpose:        d.purpose        || 'Differential Diagnosis',
    diagnosis_context: d.diagnosis_context || 'To assess cochlear vs. retrocochlear pathology',
    tdt: {
      procedure: (d.tdt && d.tdt.procedure) || DEFAULT_TDT_PROCEDURE,
      values:    (d.tdt && d.tdt.values)    ? { ...d.tdt.values }    : {},
    },
    sisi: {
      procedure: (d.sisi && d.sisi.procedure) || DEFAULT_SISI_PROCEDURE,
      right:     (d.sisi && d.sisi.right)     ? { ...d.sisi.right }  : {},
      left:      (d.sisi && d.sisi.left)      ? { ...d.sisi.left }   : {},
    },
    ablb: {
      procedure: (d.ablb && d.ablb.procedure) || DEFAULT_ABLB_PROCEDURE,
      right:     (d.ablb && d.ablb.right)     ? { ...d.ablb.right }  : {},
      left:      (d.ablb && d.ablb.left)      ? { ...d.ablb.left }   : {},
    },
    overall_conclusion: d.overall_conclusion || '',
    recommendations:    d.recommendations    || '',
    fields: d.fields || {},   // legacy compat
  };
}

// ── Interpretation --------------------------------------------------
// Each returns { verdict, label, note, tone } where tone ∈ 'neg' | 'pos' | 'mid' | 'none'
export function interpretTDT(state) {
  const values = state?.tdt?.values || {};
  const entries = TDT_FREQS.map((f) => values[String(f)]).filter((v) => v !== undefined && v !== null && v !== '');
  if (!entries.length) return { verdict: null, label: 'No data', note: 'Enter decay time per frequency.', tone: 'none' };
  const nums = entries.map(Number).filter(Number.isFinite);
  if (!nums.length) return { verdict: null, label: 'No data', note: 'Enter numeric decay time.', tone: 'none' };
  const anyDecay = nums.some((v) => v < TDT_MAX_SECONDS);
  if (!anyDecay) {
    return {
      verdict: 'negative',
      label: 'Negative (No significant decay)',
      note: 'Tone perceived for ≥ 60 seconds. Suggests no abnormal auditory fatigue.',
      tone: 'neg',
    };
  }
  return {
    verdict: 'positive',
    label: 'Positive (Abnormal decay)',
    note: 'Tone decayed before 60 s at ≥1 frequency. Consider retrocochlear evaluation.',
    tone: 'pos',
  };
}

export function interpretSISI(state) {
  const gather = (ear) => {
    const bag = state?.sisi?.[ear] || {};
    return SISI_FREQS.map((f) => bag[String(f)]).filter((v) => v !== undefined && v !== null && v !== '' && Number.isFinite(Number(v))).map(Number);
  };
  const all = [...gather('right'), ...gather('left')];
  if (!all.length) return { verdict: null, label: 'No data', note: 'Enter SISI % per frequency.', tone: 'none' };
  const maxScore = Math.max(...all);
  if (maxScore >= SISI_COCHLEAR_LOWER) {
    return {
      verdict: 'positive',
      label: 'Positive (Cochlear)',
      note: 'SISI ≥ 70 % — suggests cochlear (inner hair cell) involvement.',
      tone: 'pos',
    };
  }
  if (maxScore < SISI_NORMAL_UPPER) {
    return {
      verdict: 'negative',
      label: 'Negative (Normal)',
      note: 'SISI scores < 20 % across test frequencies. Suggests no evidence of cochlear (inner hair cell) pathology.',
      tone: 'neg',
    };
  }
  return {
    verdict: 'intermediate',
    label: 'Intermediate',
    note: 'SISI scores 20–70 % — inconclusive; correlate with other tests.',
    tone: 'mid',
  };
}

export function interpretABLB(state) {
  const perEarDiffs = (ear) => {
    const bag = state?.ablb?.[ear] || {};
    return ABLB_INTENSITIES.map((i) => {
      const m = bag[String(i)];
      if (m === undefined || m === null || m === '' || !Number.isFinite(Number(m))) return null;
      return { intensity: i, match: Number(m), diff: Number(m) - i };
    }).filter(Boolean);
  };
  const right = perEarDiffs('right');
  const left  = perEarDiffs('left');
  const rows = [...right, ...left];
  if (!rows.length) return { verdict: null, label: 'No data', note: 'Enter non-test ear match per intensity.', tone: 'none' };

  // Recruitment signature: interaural diff narrows to 0 dB at the HIGHEST
  // tested intensity, having been non-zero at the LOWEST. Check this FIRST
  // — if it fires, the "all ≤ 5 dB" fallback below never claims the case
  // as Negative (which would clinically hide the recruitment finding).
  const rightSorted = [...right].sort((a, b) => a.intensity - b.intensity);
  const leftSorted  = [...left].sort((a, b) => a.intensity - b.intensity);
  const narrows = (arr) => arr.length >= 2
    && Math.abs(arr[arr.length - 1].diff) === 0
    && Math.abs(arr[0].diff) > 0;
  if (narrows(rightSorted) || narrows(leftSorted)) {
    return {
      verdict: 'positive',
      label: 'Positive (Recruitment present)',
      note: 'Interaural difference narrows to 0 dB at higher intensities.',
      tone: 'pos',
    };
  }

  const absDiffs = rows.map((r) => Math.abs(r.diff));
  const maxAbs = Math.max(...absDiffs);
  if (maxAbs <= ABLB_NO_RECRUIT_MAX) {
    return {
      verdict: 'negative',
      label: 'Negative (No recruitment)',
      note: 'Interaural difference ≤ 5 dB across intensities. Suggests no abnormal recruitment.',
      tone: 'neg',
    };
  }
  return {
    verdict: 'partial',
    label: 'Partial recruitment',
    note: 'Difference narrows but does not reach 0 dB.',
    tone: 'mid',
  };
}

export function overallConclusion(t, s, a) {
  const arr = [t, s, a].filter((r) => r && r.verdict);
  if (!arr.length) return { label: '—', note: 'No results yet.', tone: 'none' };
  const anyPos = arr.some((r) => r.verdict === 'positive' || r.verdict === 'partial');
  const anyMid = arr.some((r) => r.verdict === 'intermediate' || r.verdict === 'partial');
  const allNeg = arr.every((r) => r.verdict === 'negative');
  if (allNeg) return {
    label: 'All special tests are within normal limits.',
    note: 'Findings do not suggest cochlear pathology (recruitment) or retrocochlear involvement. Correlate with pure tone audiometry, speech audiometry and clinical history.',
    tone: 'neg',
  };
  if (anyPos) return {
    label: 'Abnormal — clinical follow-up indicated.',
    note: 'One or more tests flagged as positive. Recommend correlation with pure tone / speech audiometry and further diagnostic workup.',
    tone: 'pos',
  };
  if (anyMid) return {
    label: 'Mixed — inconclusive findings.',
    note: 'Some tests fall in the intermediate range. Recommend repeat testing or additional diagnostics.',
    tone: 'mid',
  };
  return { label: '—', note: 'No results yet.', tone: 'none' };
}
